import React, { useEffect } from "react";
import Loader from "../components/common/loader/loader";
import { TableColumn } from "../interfaces/tables";
import Table from "../components/ui/table";
import { MdDelete, MdEdit } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import { Button } from "../components/ui/button";
import Error from "../components/common/error/error";
import TableHeaders from "../components/ui/table-headers";
import { siteSettings } from "../settings/site-settings";
import { cn, notify } from "../lib/utils";
import { PlusIcon } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  deleteSubcategory,
  listAllSubcategories,
} from "../api/admin/subcategories";
import DeleteSubcategoryDialog from "../components/subcategories/delete-subcategory-dialog";
import usePageTitle from "../hooks/usePageTitle";

function Subcategories() {
  usePageTitle("Subcategories");

  const navigate = useNavigate();
  const [subcategories, setSubcategories] = React.useState<any>([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(
    siteSettings.tables.subcategories.defaultPageSize.key
  );

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  // regenerates the api key for the project
  const [selectedSubcategoryForDelete, setSelectedSubcategoryForDelete] =
    React.useState<any | null>(null);

  const [subcategoryDeleteDialogOpen, setSubcategoryDeleteDialogOpen] =
    React.useState(false);

  const handleOpenDeleteSubcategoryDialog = (category: any) => {
    setSelectedSubcategoryForDelete(category);
    setSubcategoryDeleteDialogOpen(true);
  };

  const handleCloseDeleteSubcategoryDialog = () => {
    setSelectedSubcategoryForDelete(null);
    setSubcategoryDeleteDialogOpen(false);
  };

  const fetchSubcategories = async () => {
    const response = await listAllSubcategories({
      page,
      sort,
      search,
      pageSize,
    });

    if (!response || response.status !== 200) {
      console.log("Error fetching users");
      setError(true);
      setLoading(false);

      return;
    }

    setSubcategories(response.data?.data);
    setTableData(formatDataForTable(response.data?.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handleSubcategoryDelete = async (categoryId: string) => {
    const response = await deleteSubcategory(categoryId);

    if (!response || response.status !== 200) {
      notify({
        type: "error",
        message: "Error deleting Subcategory",
      });
      return;
    }

    notify({
      type: "success",
      message: "Subcategory deleted successfully",
    });

    setSubcategoryDeleteDialogOpen(false);
    fetchSubcategories();
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  // headers change listener
  useEffect(() => {
    fetchSubcategories();
  }, [page, sort, search, pageSize]);

  const columns: TableColumn[] = [
    {
      key: "image",
      label: "Image",
      render: (cell, row) => {
        if (cell) {
          return (
            <img
              src={cell}
              alt="category"
              className="h-[50px] w-[35px] object-cover rounded-md"
            />
          );
        } else {
          return (
            <div className="w-[35px] h-[50px] bg-borderColor rounded-md flex items-center justify-center text-gray-500">
              N/A
            </div>
          );
        }
      },
    },

    { key: "_id", label: "Internal Id" },

    { key: "internalName", label: "Internal Name" },
    { key: "displayName", label: "Display Name (EN)" },
    {
      key: "parents",
      label: "Assigned To Categories",
      render: (cell, row) => (
        <div className="w-full flex items-center /justify-between ">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((category: any, index: number) => (
              <div
                key={index}
                className={cn("text-sm w-max h-full ", {
                  "ml-1": index < 2,
                })}
              >
                {category.internalName}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}

          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((cat: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-x-2"
                          >
                            - <span>{cat.internalName}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },
    { key: "slug", label: "Slug" },

    { key: "description", label: "Description (EN)" },

    {
      key: "createdAt",
      label: "Created At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },

    {
      key: "btn",
      label: "Actions",
      fixed: true,
      render: (cell, row) => {
        return (
          <div className="flex gap-x-4">
            <Link to={`${ROUTES.SUBCATEGORY_FORM}/${row._id}`}>
              <MdEdit
                title="Edit"
                className="hover:text-primary cursor-pointer"
                size={24}
              />
            </Link>

            <MdDelete
              title="Delete"
              className="hover:text-red-600 cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenDeleteSubcategoryDialog(row);
              }}
            />
          </div>
        );
      },
    },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,
        internalName: d.internalName,
        displayName: d.displayName?.en,
        slug: d.slug,
        description: d.description?.en || "N/A",
        parents: d.parents,
        createdAt: d.createdAt,
        image: d.image,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">Subcategories</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full mx-auto flex items-end justify-end">
            <Button
              onClick={() => {
                navigate(ROUTES.SUBCATEGORY_FORM);
              }}
            >
              Create Subcategory
            </Button>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={siteSettings.tables.categories.searchByOptions}
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.categories.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={
                siteSettings.tables.subcategories.pageSizeOptions
              }
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={
                siteSettings.tables.subcategories.defaultPageSize
              }
            />
            <Table columns={columns} data={tableData} />
          </div>

          <DeleteSubcategoryDialog
            subcategory={selectedSubcategoryForDelete}
            open={subcategoryDeleteDialogOpen}
            onClose={handleCloseDeleteSubcategoryDialog}
            onSubcategoryDelete={(subcategoryId: string) => {
              handleSubcategoryDelete(subcategoryId);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Subcategories;
